import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, Divider, IconButton, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { getMesa, crearCobroMesa, revalidarCobro } from "../api/cobroCuenta";
import { getBusinessByQrId } from "../api/business";
import { getTipedsByBusinessId } from "../api/tiped";
import { STRIPE_API_KEY } from "../utils";
import { ensureTiperId } from "../utils/ensureTiper";
import { event } from "../utils/gtag";
import {
  getActiveUnidades,
  buildVisibleDestinoChips,
  shouldShowChipSelector,
  isSoloRepartoColectivo,
  filterTipedsForNuclio,
  sortTipedsForDestino,
} from "../utils/destinoChips";
import BusinessBrandingHeader, { getBusinessBrandPrimary } from "../components/BusinessBrandingHeader";
import PoweredByTipify from "../components/PoweredByTipify";
import logo from "../assets/logo.png";

const stripePromise = loadStripe(STRIPE_API_KEY);

/** Errores del servidor que tienen su propio mensaje en pantalla. */
const MENSAJES_ERROR = {
  MESA_NO_ENCONTRADA: "cuentaMesa.noValido",
  POS_SIN_CUENTA: "cuentaMesa.sinCuenta",
  POS_CUENTA_PAGANDOSE: "cuentaMesa.pagandose",
  POS_CUENTA_PAGADA: "cuentaMesa.pagada",
  POS_NO_DISPONIBLE: "cuentaMesa.noDisponible",
  POS_MODULO_INACTIVO: "cuentaMesa.noDisponible",
  POS_NO_CONECTADO: "cuentaMesa.noDisponible",
};

function stripeLocale(lang) {
  const code = String(lang || "en").split("-")[0].toLowerCase();
  return code === "ca" ? "es" : code;
}

function useDinero() {
  const { i18n } = useTranslation();
  return useCallback(
    (cents, moneda = "eur") =>
      new Intl.NumberFormat(i18n.language || "es", { style: "currency", currency: String(moneda).toUpperCase() }).format(
        Number(cents || 0) / 100,
      ),
    [i18n.language],
  );
}

function nombreUnidad(nombre, lang) {
  if (!nombre) return "";
  if (typeof nombre === "string") return nombre;
  return nombre[String(lang || "es").split("-")[0]] || nombre.es || Object.values(nombre)[0] || "";
}

function nombreTiped(t) {
  return String(t?.alias || "").trim() || `${t?.nombre || ""} ${t?.apellidos || ""}`.trim();
}

/** Líneas de la cuenta y su total. */
function Tiquet({ cuenta, dinero, t }) {
  return (
    <Box sx={{ border: "1px solid #eee", borderRadius: 2, p: 2, mb: 2 }}>
      {(cuenta.lineas || []).map((l, i) => (
        <Box key={i} sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.5 }}>
          <Typography variant="body2" sx={{ color: "#444" }}>
            {l.cantidad % 1 === 0 ? l.cantidad : l.cantidad.toFixed(2)} × {l.nombre}
          </Typography>
          <Typography variant="body2" sx={{ color: "#444", whiteSpace: "nowrap" }}>
            {dinero(l.totalCents, cuenta.moneda)}
          </Typography>
        </Box>
      ))}
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontWeight: 600 }}>{t("cuentaMesa.totalCuenta")}</Typography>
        <Typography sx={{ fontWeight: 600 }}>{dinero(cuenta.pendienteCents, cuenta.moneda)}</Typography>
      </Box>
    </Box>
  );
}

function ResumenPago({ cobro, dinero, t }) {
  const fila = (label, cents, fuerte = false) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.25 }}>
      <Typography sx={{ fontWeight: fuerte ? 700 : 400 }}>{label}</Typography>
      <Typography sx={{ fontWeight: fuerte ? 700 : 400 }}>{dinero(cents, cobro.currency)}</Typography>
    </Box>
  );
  return (
    <Box sx={{ mb: 2 }}>
      {fila(t("cuentaMesa.resumenCuenta"), cobro.amounts.cuentaCents)}
      {cobro.amounts.tipCents > 0 && fila(t("cuentaMesa.resumenPropina"), cobro.amounts.tipCents)}
      {cobro.amounts.feeCents > 0 && fila(t("cuentaMesa.resumenGastos"), cobro.amounts.feeCents)}
      <Divider sx={{ my: 1 }} />
      {fila(t("cuentaMesa.resumenTotal"), cobro.amounts.totalCents, true)}
    </Box>
  );
}

function FormularioPago({ codigo, cobro, brandPrimary, onCuentaCambiada }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dinero = useDinero();
  const [procesando, setProcesando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  const pagar = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || procesando) return;
    setProcesando(true);
    setMensaje(null);
    event({ action: "click_dar_propina", category: "Pagos", label: "CuentaMesa", value: cobro.amounts.totalCents / 100 });
    try {
      const { error: errorFormulario } = await elements.submit();
      if (errorFormulario) {
        setMensaje(errorFormulario.message);
        setProcesando(false);
        return;
      }
      // Última comprobación: si el camarero añadió algo, no se cobra la cuenta vieja
      const { data: comprobacion } = await revalidarCobro(cobro.cobroId);
      if (comprobacion.cambiada) {
        onCuentaCambiada();
        return;
      }
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/m/${codigo}/ok/${cobro.cobroId}`,
          payment_method_data: { billing_details: { address: { country: "ES", postal_code: "" } } },
        },
        redirect: "if_required",
      });
      if (error) {
        event({ action: "error_pago", category: "Pagos", label: `CuentaMesa: ${error.decline_code || error.code || error.type || "desconocido"}` });
        setMensaje(error.message);
        setProcesando(false);
        return;
      }
      if (paymentIntent && ["succeeded", "processing"].includes(paymentIntent.status)) {
        navigate(`/m/${codigo}/ok/${cobro.cobroId}`, { replace: true });
        return;
      }
      setProcesando(false);
    } catch (err) {
      event({ action: "error_pago", category: "Pagos", label: "CuentaMesa: excepcion" });
      setMensaje(t("cuentaMesa.errorPago"));
      setProcesando(false);
    }
  };

  return (
    <form onSubmit={pagar}>
      <PaymentElement options={{ fields: { billingDetails: { address: { country: "never", postalCode: "never" } } } }} />
      {mensaje && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {mensaje}
        </Alert>
      )}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={!stripe || procesando}
        sx={{ mt: 3, py: 1.5, borderRadius: 999, bgcolor: brandPrimary, fontSize: 16, textTransform: "none", "&:hover": { bgcolor: brandPrimary } }}
      >
        {t("cuentaMesa.confirmar", { importe: dinero(cobro.amounts.totalCents, cobro.currency) })}
      </Button>
    </form>
  );
}

export default function CuentaMesaScreen() {
  const { codigo } = useParams();
  const { t, i18n } = useTranslation();
  const dinero = useDinero();

  const [cargando, setCargando] = useState(true);
  const [datos, setDatos] = useState(null);
  const [negocio, setNegocio] = useState(null);
  const [nuclio, setNuclio] = useState(null);
  const [tipeds, setTipeds] = useState([]);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);

  const [propina, setPropina] = useState(10);
  const [otraCantidad, setOtraCantidad] = useState("");
  const [destino, setDestino] = useState(null);
  const [creando, setCreando] = useState(false);
  const [cobro, setCobro] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const { data } = await getMesa(codigo);
      setDatos(data);
      if (data.qrId && !negocio) {
        const res = await getBusinessByQrId(data.qrId);
        if (res.status === 200) {
          const business = res.data.business || res.data;
          setNegocio(business);
          setNuclio(res.data.nuclioNegocio || null);
          const ts = await getTipedsByBusinessId(business._id);
          if (ts.status === 200) setTipeds(ts.data || []);
        }
      }
    } catch (err) {
      const code = err?.response?.data?.code;
      setError(MENSAJES_ERROR[code] || "cuentaMesa.noDisponible");
    } finally {
      setCargando(false);
    }
  }, [codigo, negocio]);

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigo]);

  const chips = useMemo(() => {
    if (!nuclio) return [];
    const unidades = getActiveUnidades(nuclio);
    return buildVisibleDestinoChips(nuclio, unidades, sortTipedsForDestino(filterTipedsForNuclio(tipeds, nuclio)));
  }, [nuclio, tipeds]);

  useEffect(() => {
    if (!destino && chips.length && (isSoloRepartoColectivo(chips) || chips.some((c) => c.type === "colectivo"))) {
      setDestino({ type: "colectivo" });
    }
  }, [chips, destino]);

  const cuenta = datos?.cuenta;
  const propinaCents = useMemo(() => {
    if (!cuenta || propina === "none") return 0;
    if (propina === "custom") return Math.round(Number(String(otraCantidad).replace(",", ".")) * 100) || 0;
    return Math.round((cuenta.pendienteCents * propina) / 100);
  }, [cuenta, propina, otraCantidad]);

  const crear = async () => {
    if (!cuenta || creando) return;
    if (chips.length && shouldShowChipSelector(chips) && propina !== "none" && !destino) {
      setAviso(t("cuentaMesa.eligeDestino"));
      return;
    }
    setCreando(true);
    setAviso(null);
    try {
      const tiper = await ensureTiperId().catch(() => null);
      const body = { tiper };
      if (propina === "none") body.sinPropina = true;
      else if (propina === "custom") body.importePropina = propinaCents / 100;
      else body.porcentajePropina = propina;
      if (destino?.type === "colectivo") body.repartoColectivo = true;
      if (destino?.type === "tiped") body.tiped = destino.id;
      if (destino?.type === "unidad") body.unidadTrabajo = destino.id;
      const { data } = await crearCobroMesa(codigo, body);
      setCobro(data);
      event({ action: "carga_pantalla_pago", category: "Pantallas", label: "CuentaMesa" });
    } catch (err) {
      const code = err?.response?.data?.code;
      if (MENSAJES_ERROR[code]) setAviso(t(MENSAJES_ERROR[code]));
      else setAviso(err?.response?.data?.message || t("cuentaMesa.errorPago"));
    } finally {
      setCreando(false);
    }
  };

  const cuentaCambiada = async () => {
    setCobro(null);
    setAviso(t("cuentaMesa.cuentaCambiada"));
    await cargar();
  };

  const brandPrimary = getBusinessBrandPrimary(negocio) || "#66bc5f";

  if (cargando && !datos) {
    return (
      <div className="loading-container">
        <img src={logo} alt="Tipify" className="loading-logo pulse-effect" />
      </div>
    );
  }

  const pantalla = (contenido) => (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: "#fff", px: { xs: 2.5, sm: 4 }, py: 3, maxWidth: 560, mx: "auto" }}>
      {negocio && <BusinessBrandingHeader business={negocio} showLanguageSelector />}
      {contenido}
      <PoweredByTipify />
    </Box>
  );

  if (error) {
    return pantalla(
      <Alert severity="info" sx={{ mt: 3 }}>
        {t(error)}
      </Alert>,
    );
  }

  const titulo = (
    <Typography variant="h5" sx={{ fontWeight: 700, mt: 2, mb: 2, textAlign: "center" }}>
      {t("cuentaMesa.mesa", { nombre: datos?.mesa?.nombre })}
    </Typography>
  );

  if (!cuenta) {
    return pantalla(
      <>
        {titulo}
        <Alert severity="info">{t(datos?.pagada ? "cuentaMesa.pagada" : "cuentaMesa.sinCuenta")}</Alert>
        <Button fullWidth sx={{ mt: 2, textTransform: "none" }} onClick={cargar}>
          {t("cuentaMesa.actualizar")}
        </Button>
      </>,
    );
  }

  if (cobro) {
    return pantalla(
      <>
        <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
          <IconButton onClick={() => setCobro(null)} aria-label={t("cuentaMesa.volver")}>
            <ArrowBackIcon />
          </IconButton>
          <Typography sx={{ fontWeight: 700 }}>{t("cuentaMesa.mesa", { nombre: datos?.mesa?.nombre })}</Typography>
        </Box>
        <ResumenPago cobro={cobro} dinero={dinero} t={t} />
        <Elements
          key={cobro.clientSecret}
          stripe={stripePromise}
          options={{
            clientSecret: cobro.clientSecret,
            locale: stripeLocale(i18n.language),
            appearance: { theme: "stripe", variables: { colorPrimary: brandPrimary, borderRadius: "8px" } },
          }}
        >
          <FormularioPago codigo={codigo} cobro={cobro} brandPrimary={brandPrimary} onCuentaCambiada={cuentaCambiada} />
        </Elements>
      </>,
    );
  }

  const opciones = datos?.percentOptions || [5, 10, 15, 20];
  return pantalla(
    <>
      {titulo}
      {aviso && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {aviso}
        </Alert>
      )}
      {datos?.pagandose && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("cuentaMesa.pagandose")}
        </Alert>
      )}
      <Tiquet cuenta={cuenta} dinero={dinero} t={t} />

      <Typography sx={{ fontWeight: 700, mt: 3 }}>{t("cuentaMesa.propinaTitulo")}</Typography>
      <Typography variant="body2" sx={{ color: "#666", mb: 1.5 }}>
        {t("cuentaMesa.propinaSub")}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
        {opciones.map((pct) => (
          <Button
            key={pct}
            variant={propina === pct ? "contained" : "outlined"}
            onClick={() => {
              setPropina(pct);
              event({ action: "select_tip_amount", category: "Donaciones", label: "CuentaMesa", value: pct });
            }}
            sx={{ textTransform: "none", flexDirection: "column", py: 1, bgcolor: propina === pct ? brandPrimary : undefined, borderColor: brandPrimary, color: propina === pct ? "#fff" : "#333", "&:hover": { bgcolor: propina === pct ? brandPrimary : undefined } }}
          >
            <span style={{ fontWeight: 700 }}>{pct} %</span>
            <span style={{ fontSize: 12 }}>{dinero(Math.round((cuenta.pendienteCents * pct) / 100), cuenta.moneda)}</span>
          </Button>
        ))}
        <Button
          variant={propina === "custom" ? "contained" : "outlined"}
          onClick={() => setPropina("custom")}
          sx={{ textTransform: "none", bgcolor: propina === "custom" ? brandPrimary : undefined, borderColor: brandPrimary, color: propina === "custom" ? "#fff" : "#333", "&:hover": { bgcolor: propina === "custom" ? brandPrimary : undefined } }}
        >
          {t("cuentaMesa.otraCantidad")}
        </Button>
        <Button
          variant={propina === "none" ? "contained" : "outlined"}
          onClick={() => setPropina("none")}
          sx={{ textTransform: "none", bgcolor: propina === "none" ? "#888" : undefined, borderColor: "#bbb", color: propina === "none" ? "#fff" : "#666", "&:hover": { bgcolor: propina === "none" ? "#888" : undefined } }}
        >
          {t("cuentaMesa.sinPropina")}
        </Button>
      </Box>
      {propina === "custom" && (
        <TextField
          fullWidth
          size="small"
          type="number"
          inputProps={{ min: 0.5, step: 0.5, inputMode: "decimal" }}
          label={t("cuentaMesa.importePropina")}
          value={otraCantidad}
          onChange={(e) => setOtraCantidad(e.target.value)}
          sx={{ mt: 1.5 }}
        />
      )}

      {propina !== "none" && shouldShowChipSelector(chips) && (
        <>
          <Typography sx={{ fontWeight: 700, mt: 3, mb: 1 }}>{t("cuentaMesa.destinoTitulo")}</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {chips.map((c) => {
              const activo = destino?.type === c.type && (c.type === "colectivo" || String(destino.id) === String(c.id));
              const label =
                c.type === "colectivo"
                  ? t("cuentaMesa.todoEquipo")
                  : c.type === "tiped"
                    ? nombreTiped(c.tiped)
                    : nombreUnidad(getActiveUnidades(nuclio).find((u) => String(u.cargoId) === String(c.id))?.name, i18n.language);
              return (
                <Chip
                  key={`${c.type}-${c.id || "todos"}`}
                  label={label}
                  onClick={() => setDestino({ type: c.type, id: c.id })}
                  sx={{ bgcolor: activo ? brandPrimary : "#f2f2f2", color: activo ? "#fff" : "#333" }}
                />
              );
            })}
          </Box>
        </>
      )}

      <Typography variant="body2" sx={{ color: "#666", mt: 2 }}>
        {t("cuentaMesa.propinaNota")}
      </Typography>

      <Button
        fullWidth
        variant="contained"
        disabled={creando || datos?.pagandose || (propina === "custom" && propinaCents < 50)}
        onClick={crear}
        sx={{ mt: 3, mb: 2, py: 1.5, borderRadius: 999, bgcolor: brandPrimary, fontSize: 16, textTransform: "none", "&:hover": { bgcolor: brandPrimary } }}
      >
        {t("cuentaMesa.pagar", { importe: dinero(cuenta.pendienteCents + propinaCents, cuenta.moneda) })}
      </Button>
    </>,
  );
}
